/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Box, Button, Grid } from '@mui/material';
import { Info, SignPlus } from '@jda/lui-common-icon-library-mui5';
import {  LuiEmptyState, LuiSearch, LuiToolbar } from '@jda/lui-common-component-library-mui5';
import { selectAllAssignedPermissions } from '../../store/auth/authSelector';
import { checkPermission } from '../../shared/CheckPermissions/CheckPermission';
import { CheckViewPermission } from '../../shared/CheckPermissions/CheckViewPermission';
import { permissions } from '../../shared/CheckPermissions/constants';
import { getRoleMappingData } from '../../services/roleMapping';
import RoleMappingList from '../../components/RoleMappingList';
import SnackbarAlert from '../../shared/SnackbarAlert';
import AddRoleMappingDialog from '../../components/AddRoleMappingDialog';
import { setSnackBarMessage } from '../../store/snackBar/snackBarActionCreator';
import { getUserRealmSettings } from '../../services/setting';
import { getEntitlementServiceIdps } from '../../services/user';
import { SNACKBAR_ALERT_DEFAULT_PROPS } from '../Users/constants';
import {
  ADD_ROLE_MAPPING,
  ROLE_MAPPINGS,
  SEARCH_ROLE,
  ENABLE_ROLE_MAPPING_MESSAGE,
  NO_ROLE_MAPPING_MESSAGE,
  EMPTY_SCREEN_TITLE
} from './constants';
import { useStyles } from './styles';
import {
 MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton
} from 'material-react-table';


const RoleMappings = () => {
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const [roleMappingData, setRoleMappingData] = useState<any>([]);
  const [filteredRoleMappingData, setFilteredRoleMappingData] = useState<any>([]);
  const [enableRoleMapping, setEnableRoleMapping] = useState<any>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [showLoader, setShowLoader] = useState(true);
  const [openModal, setOpenCloseModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editData, setEditData] = useState();
  const assignedPermissions = useSelector(selectAllAssignedPermissions);
  const readWritePermission =
    assignedPermissions &&
    assignedPermissions.permissions &&
    checkPermission([permissions.idp_role.read_write], assignedPermissions.permissions);
  const readPermission =
    assignedPermissions &&
    assignedPermissions.permissions &&
    checkPermission([permissions.idp_role.read], assignedPermissions.permissions);

  const getAllRoleMappingData = async () => {
    setShowLoader(true);
    const response = await getEntitlementServiceIdps();
    getRoleMappingData().then(res => {
      const result = res.map((item: any) => {
        let match = response?.idps?.find((realm: any) => realm.id === item.idpId);
        return {
          realmName: item.byRole.name,
          name: item.name,
          idpName: match ? match.name : '',
          idpId: item.idpId,
          byRoleId: item.byRole.id,
          id: item.id
        };
      });
      setRoleMappingData(result);
      setFilteredRoleMappingData(result);
      setShowLoader(false);
    });
  };

  useEffect(() => {
    getUserRealmSettings().then(res => {
      if (res?.enableIdpRoleMapping) {
        setEnableRoleMapping(res?.enableIdpRoleMapping);
      }
      getAllRoleMappingData();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const displaySnackbar = (message: string, severity: 'success' | 'error') => {
    dispatch(
      setSnackBarMessage({
        autoHideDuration: 5000,
        showSnackbar: true,
        message: message,
        severity: severity
      })
    );
  };

  const hideSnackbar = () => {
    dispatch(
      setSnackBarMessage({
        ...SNACKBAR_ALERT_DEFAULT_PROPS,
        showSnackbar: false
      })
    );
  };
  const onEdit = (rowData: any) => {
    setEditData(rowData);
    setIsEdit(true);
    setOpenCloseModal(!openModal);
  };

  const openAddRoleMappingDialog = () => {
    setOpenCloseModal(!openModal);
    setIsEdit(false);
  };

  const saveRoleMapping = () => {
    setOpenCloseModal(!openModal);
    getAllRoleMappingData();
  };

  const onSearchTextBoxChange = (e: any) => {
    const searchString = e.target.value;
    setSearchValue(searchString);
    if (!searchString.length) setFilteredRoleMappingData(roleMappingData);
    else {
      const filteredRoleMappingList = roleMappingData.filter((role: any) => {
        return role.name && role.name.toUpperCase().includes(searchString.toUpperCase());
      });
      setFilteredRoleMappingData(filteredRoleMappingList);
    }
  };

  const handleOnClear = () => {
    setFilteredRoleMappingData(roleMappingData);
    setSearchValue('');
  };

  const extendedToolbar = ({ table }) => {
    const leftComponents = {
      component: (
        <Box className={classes.searchBarWrap}>
          <LuiSearch
            size="small"
            value={searchValue}
            onChange={onSearchTextBoxChange}
            placeholder={SEARCH_ROLE}
            onClear={handleOnClear}
          />
        </Box>
      )
    };
    const rightComponents = {
      component: (
        <Box>
          {enableRoleMapping && (
            <CheckViewPermission permission={[permissions.idp_role.read_write]}>
              <Button
                size="small"
                color="primary"
                disableElevation
                variant="contained"
                startIcon={<SignPlus />}
                data-testid="add-user-button"
                onClick={openAddRoleMappingDialog}
              >
                {ADD_ROLE_MAPPING}
              </Button>
            </CheckViewPermission>
          )}
          <ShowHideColumnsButton table={table} />
          <ToggleDensePaddingButton table={table} />
          <ToggleFullScreenButton table={table} />
        </Box>
      )
    };
    return <LuiToolbar className={classes.toolbarPanel} leftZone={leftComponents} rightZone={rightComponents} />;
  };
const emptyTableFallback = () => {
    return (
      <Box my={5}>
        <LuiEmptyState  subHeading={readWritePermission ? EMPTY_SCREEN_TITLE : NO_ROLE_MAPPING_MESSAGE} />
      </Box>
    );
  };
  return (
    <>
      <CheckViewPermission permission={[permissions.idp_role.read, permissions.idp_role.read_write]}>
        <Grid className={classes.root}>
          {!enableRoleMapping && !showLoader && (
            <Alert icon={<Info />} elevation={0} severity={'info'} variant={'outlined'} >
            {readWritePermission ? ENABLE_ROLE_MAPPING_MESSAGE : NO_ROLE_MAPPING_MESSAGE}
          </Alert>)}
          <Grid container className={classes.mappingList}>
            <Grid item xs={12} sm={12}>
              <RoleMappingList
                onEdit={onEdit}
                loading={showLoader}
                roleMappingData={filteredRoleMappingData}
                enableRoleMapping={enableRoleMapping}
                readPermission={readPermission}
                readWritePermission={readWritePermission}
                refreshOnDelete={getAllRoleMappingData}
                extendedToolbar={extendedToolbar}
                emptyTableFallback={emptyTableFallback}
              />
            </Grid>
          </Grid>
        </Grid>
        {openModal && (
          <AddRoleMappingDialog
            edit={isEdit}
            editData={editData}
            openModal={openModal}
            onSave={saveRoleMapping}
            hideSnackbar={hideSnackbar}
            displaySnackbar={displaySnackbar}
            openCloseModal={setOpenCloseModal}
          />
        )}
        <Grid container>
          <SnackbarAlert />
        </Grid>
      </CheckViewPermission>
    </>
  );
};

const RoleMapping = {
  component: RoleMappings,
  name: ROLE_MAPPINGS
};

export default RoleMapping;
